import { useTranslation } from "react-i18next";
import { AlertNotificationWithAlertRowDetails } from "~/utils/db/alertNotification.db.server";
import { Link } from "@remix-run/react";
import DateUtils from "~/utils/shared/DateUtils";
import AlarmIcon from "~/components/ui/icons/notifications/AlarmIcon";
import { useState } from "react";
import ChevronLeftIcon from "~/components/ui/icons/ChevronLeftIcon";
import RightIcon from "~/components/ui/icons/RightIcon";

interface Props {
  items: AlertNotificationWithAlertRowDetails[];
  withTitle?: boolean;
}

export default function RowAlertNotifications({ items, withTitle = true }: Props) {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 5;
  const maxTotalItems = 20;
  
  // Sort and limit items to max 10 total
  const processedItems = items
    .slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, maxTotalItems);
    
  // Get current page items
  const paginatedItems = () => {
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return processedItems.slice(startIndex, endIndex);
  };
  
  const totalPages = Math.ceil(processedItems.length / itemsPerPage);
  
  const nextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  // If there are no notifications, don't render the component
  if (items.length === 0) {
    return null;
  }

  return (
    <section className="relative">
      <div className="space-y-3">
        <div className="divide-y divide-gray-200">
          {withTitle && (
            <div className="flex items-center justify-between pb-2">
              <h2 id="alerts-title" className="text-sm font-medium text-gray-900">
                {t("Recent Alerts")}
              </h2>
              
              {totalPages > 1 && (
                <div className="flex items-center space-x-1">
                  <button
                    onClick={prevPage}
                    disabled={currentPage === 0}
                    className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label="Previous page"
                    title="Previous page"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  <span className="text-xs text-gray-500">
                    {currentPage + 1}/{totalPages}
                  </span>
                  <button
                    onClick={nextPage}
                    disabled={currentPage === totalPages - 1}
                    className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label="Next page"
                    title="Next page"
                  >
                    <RightIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          )}
          
          <div className="pt-4">
            {processedItems.length === 0 ? (
              <p className="text-sm text-gray-500">{t("No recent alerts")}</p>
            ) : (
              <ul className="space-y-4">
                {paginatedItems().map((item) => (
                  <li key={item.id}>
                    <Link 
                      to={`/app/${item.tenantId}/alertNotifications/${item.id}`}
                      className="block rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-all hover:border-gray-300 hover:shadow"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <div className="relative">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100">
                              <AlarmIcon className="h-4 w-4 text-gray-500" />
                            </div>
                          </div>
                        </div>
                        
                        <div className="min-w-0 flex-1">
                          <div className="text-sm">
                            {item.alertRow && item.alertRow.values.map((value) => {
                              if (value.propertyName === "name") {
                                return (
                                  <p key={value.propertyName} className="font-medium text-gray-900 line-clamp-1">
                                    {value.textValue}
                                  </p>
                                );
                              }
                              return null;
                            })}
                            
                            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                              <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600">
                                {item.status}
                              </span>
                              
                              {item.sentAt && (
                                <time dateTime={DateUtils.dateYMDHMS(item.sentAt)} title={DateUtils.dateYMDHMS(item.sentAt)}>
                                  Sent: {DateUtils.dateAgo(item.sentAt)}
                                </time>
                              )}
                              
                              {item.createdAt && (
                                <time dateTime={DateUtils.dateYMDHMS(item.createdAt)} title={DateUtils.dateYMDHMS(item.createdAt)}>
                                  Created: {DateUtils.dateAgo(item.createdAt)}
                                </time>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </section>
  );
} 